// Security utilities for input sanitization and validation

/**
 * Sanitizes input string by removing potentially dangerous characters
 * @param input - The input string to sanitize
 * @returns Sanitized string
 */
export const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }
  
  // Remove HTML tags and potentially dangerous characters
  return input
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/[<>'"&]/g, '') // Remove potentially dangerous characters
    .trim();
};

/**
 * Validates username format (allows email, phone number, or CMS ID)
 * @param username - Username to validate
 * @returns Validation result with error message if invalid
 */
export const validateUsername = (username: string): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(username);
  
  if (!sanitized) {
    return { isValid: false, error: 'TK đăng nhập không được để trống' };
  }
  
  if (sanitized.length < 3) {
    return { isValid: false, error: 'TK đăng nhập phải có ít nhất 3 ký tự' };
  }
  
  if (sanitized.length > 50) {
    return { isValid: false, error: 'TK đăng nhập không được vượt quá 50 ký tự' };
  }
  
  // Allow email format (contains @ and .)
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sanitized);
  
  // Allow phone number format (starts with 0 and contains only digits, spaces, hyphens, parentheses)
  const isPhoneNumber = /^[\d\s\-\(\)]+$/.test(sanitized) && sanitized.replace(/\D/g, '').length >= 8;
  
  // Allow CMS ID format (alphanumeric, underscores, hyphens)
  const isCmsId = /^[a-zA-Z0-9_-]+$/.test(sanitized);
  
  // Accept if it matches any of the formats
  if (!isEmail && !isPhoneNumber && !isCmsId) {
    return { isValid: false, error: 'TK đăng nhập phải là email, số điện thoại hoặc mã TK đăng nhập hợp lệ' };
  }
  
  return { isValid: true };
};

/**
 * Validates password strength
 * @param password - Password to validate
 * @returns Validation result with error message if invalid
 */
export const validatePassword = (password: string): { isValid: boolean; error?: string } => {
  if (!password) {
    return { isValid: false, error: 'Mật khẩu không được để trống' };
  }
  
  if (password.length < 6) {
    return { isValid: false, error: 'Mật khẩu phải có ít nhất 6 ký tự' };
  }
  
  if (password.length > 128) {
    return { isValid: false, error: 'Mật khẩu không được vượt quá 128 ký tự' };
  }
  
  return { isValid: true };
};

/**
 * Rate limiting utility
 */
export class RateLimiter {
  private attempts: Map<string, { count: number; resetTime: number }> = new Map();
  private readonly maxAttempts: number;
  private readonly windowMs: number;

  constructor(maxAttempts: number = 5, windowMs: number = 15 * 60 * 1000) { // 5 attempts per 15 minutes
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  isAllowed(identifier: string): boolean {
    const now = Date.now();
    const attempt = this.attempts.get(identifier);

    if (!attempt) {
      this.attempts.set(identifier, { count: 1, resetTime: now + this.windowMs });
      return true;
    }

    if (now > attempt.resetTime) {
      // Reset window
      this.attempts.set(identifier, { count: 1, resetTime: now + this.windowMs });
      return true;
    }

    if (attempt.count >= this.maxAttempts) {
      return false;
    }

    attempt.count++;
    return true;
  }

  getRemainingAttempts(identifier: string): number {
    const attempt = this.attempts.get(identifier);
    if (!attempt) return this.maxAttempts;
    
    const now = Date.now();
    if (now > attempt.resetTime) return this.maxAttempts;
    
    return Math.max(0, this.maxAttempts - attempt.count);
  }

  getResetTime(identifier: string): number {
    const attempt = this.attempts.get(identifier);
    if (!attempt) return 0;
    
    const now = Date.now();
    if (now > attempt.resetTime) return 0;
    
    return attempt.resetTime - now;
  }
}

// Global rate limiter instance
export const loginRateLimiter = new RateLimiter(5, 15 * 60 * 1000); // 5 attempts per 15 minutes
